import { useEffect, useMemo, useState } from "react";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { Container, type ISourceOptions } from "@tsparticles/engine";
import { loadConfettiPreset } from "@tsparticles/preset-confetti";
import PlayerType from "../../models/player";
import FocusLock from "react-focus-lock";
import { RemoveScroll } from "react-remove-scroll";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../app/hooks";
import { socket } from "../../socket";
import { motion } from "motion/react";

import styles from "./Congratulations.module.css";

interface Props {
  winner: PlayerType;
}

function Congratulations({ winner }: Props) {
  const { t } = useTranslation();

  const roomId = useAppSelector((state) => state.user.roomId);

  const [init, setInit] = useState(false);
  const [displayWinner, setDisplayWinner] = useState(false);

  const CONFETTI_DURATION = 3500;

  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadConfettiPreset(engine);
    }).then(() => setInit(true));
  }, []);

  const options: ISourceOptions = useMemo(
    () =>
      ({
        preset: "confetti",
        fullScreen: { enable: true, zIndex: 999 },
      }) as unknown as ISourceOptions,
    [],
  );

  const particlesLoaded = async (container?: Container): Promise<void> => {
    if (!container) return;

    container.play();

    setTimeout(() => {
      container.stop();
      setDisplayWinner(true);
    }, CONFETTI_DURATION);
  };

  function playAgain() {
    socket.emit("play again", roomId, winner.id);
  }

  return (
    <div className={styles.locker}>
      <FocusLock returnFocus>
        <RemoveScroll>
          {init && !displayWinner && (
            <Particles
              id="tsparticles"
              particlesLoaded={particlesLoaded}
              options={options}
            />
          )}

          {displayWinner && (
            <motion.div
              className={styles.backdrop}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <motion.div
                className={styles.playAgain}
                style={{ transformOrigin: "50% 50%" }}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  delay: 0.15,
                  type: "spring",
                  stiffness: 140,
                  damping: 12,
                }}
              >
                <button className={styles.playAgainButton} onClick={playAgain}>
                  {t("Congratulations.playAgain")}
                </button>
              </motion.div>
              <motion.div
                className={styles.wrapper}
                style={{ transformOrigin: "50% 50%" }}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  delay: 0.15,
                  type: "spring",
                  stiffness: 140,
                  damping: 12,
                }}
              >
                <motion.div
                  className={styles.title}
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {t("Congratulations.title")}
                </motion.div>

                <motion.div
                  className={styles.card}
                  animate={{
                    boxShadow: [
                      "0 0 0px rgba(255,255,255,0)",
                      "0 0 25px rgba(255,255,255,.8)",
                      "0 0 0px rgba(255,255,255,0)",
                    ],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <div className={styles.top}>
                    <span>{t("Congratulations.winner")}</span>
                  </div>

                  <div className={styles.name}>
                    <span>{winner.name}</span>
                  </div>

                  <div className={styles.bottom}>
                    <span>{t("Congratulations.winner")}</span>
                  </div>
                </motion.div>

                <motion.div
                  className={styles.title}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.45 }}
                >
                  {t("Congratulations.title")}
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </RemoveScroll>
      </FocusLock>
    </div>
  );
}

export default Congratulations;
